import { useCallback } from "react";

import { useLocalization } from "@fluent/react";

import { PAYMENT_ACTIVITY_ROUTE } from "../../../../components/PaymentActivity/routes";
import { useSidebarMoneyMovementMethods } from "../../transfers";

import type App from "byzantine/src/App";
import type { ReactLocalization } from "@fluent/react";
import type { MoneyMovementFeaturesData } from "../../transfers/types";
import type { SidebarOptionItem, SidebarRootItem } from "../sidebar.types";

const PAYMENT_ACTIVITY_ROUTE_PREFIX = `/${PAYMENT_ACTIVITY_ROUTE}`;

/**
 * Prefixes the route with /transfer unless it already starts
 * with the payment-activity path (e.g. /payment_activity/bulk/new).
 */
const resolveTransferRoute = (
  route: string | undefined
): string | undefined => {
  if (!route) return undefined;
  if (route.startsWith(`${PAYMENT_ACTIVITY_ROUTE_PREFIX}/`)) return route;
  return `/transfer${route}`;
};

const isZelleOption = (data: MoneyMovementFeaturesData["data"]): boolean =>
  Boolean(
    data?.route?.includes("zelle") ||
      data?.translationDescription?.includes("zelle")
  );

const buildZelleItem = (
  data: NonNullable<MoneyMovementFeaturesData["data"]>
): SidebarOptionItem => ({
  type: "standard",
  route: resolveTransferRoute(data.route) || "",
  icon: "zelle-outline",
  title: "Zelle ®",
});

const buildStandardTransferItem = (
  data: NonNullable<MoneyMovementFeaturesData["data"]>,
  l10n: ReactLocalization
): SidebarOptionItem | null => {
  const route = resolveTransferRoute(data.route);
  if (!route) return null;

  return {
    type: "standard",
    route,
    icon: data.icon,
    title:
      typeof data.title === "string"
        ? l10n.getString(data.translationTitle || "", null, data.title)
        : data.title,
  };
};

const buildMoneyMovementNAFItem = (
  moneyMovementNafApp: App
): SidebarOptionItem => ({
  // `nafData` is already deserialized by useSidebarMoneyMovementMethods,
  // so we use the App instance directly instead of deserializing again.
  type: "naf",
  nafOption: { discriminator: "app", app: moneyMovementNafApp },
});

const isValidOption = (
  item: SidebarOptionItem | null
): item is SidebarOptionItem => {
  if (!item) return false;
  if (item.type === "standard") {
    // Guard against a malformed "/transferundefined" route that can result
    // when an upstream route resolves to the literal string "undefined".
    return Boolean(
      item.title && item.route && !item.route.includes("transferundefined")
    );
  }
  return true;
};

/**
 * Hook that returns a builder for the pay-and-transfer menu item.
 *
 * Internalizes `useLocalization` and `useSidebarMoneyMovementMethods`
 * so the caller doesn't need to manage these dependencies.
 *
 * Converts MoneyMovementFeaturesData[] into SidebarOptionItem[],
 * correctly handling money-movement NAF apps with OAuth flow
 * (rendered via SidebarNAFItem -> AppAuthorize).
 */
export const useTransferMenuItem = () => {
  const { l10n } = useLocalization();
  const transferOptions = useSidebarMoneyMovementMethods();

  return useCallback(
    (menuItem: API.SidebarMenuItem_Resolved): SidebarRootItem => {
      const menuOptions = transferOptions
        .map(
          ({
            data,
            nafData: moneyMovementNafApp,
          }): SidebarOptionItem | null => {
            if (data) {
              return isZelleOption(data)
                ? buildZelleItem(data)
                : buildStandardTransferItem(data, l10n);
            }

            if (moneyMovementNafApp) {
              return buildMoneyMovementNAFItem(moneyMovementNafApp);
            }

            return null;
          }
        )
        .filter(isValidOption);

      return { ...menuItem, menuOptions };
    },
    [l10n, transferOptions]
  );
};
